import * as React from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import nl from 'date-fns/esm/locale/nl';
import * as Moment from 'moment';
import { DateFormatter } from '@fnv-core/formatting/date';
import { DateParser } from '@fnv-core/parsing';
import { isAndroid, isIE11, isIOS, Slug } from '@fnv-core/utils';
import { FormControlProps } from "../interfaces/FormControlProps";
import { FormFieldStateHelpers } from "./helpers/FormFieldStateHelpers";
import { FormStylingHelpers } from "./helpers/FormStylingHelpers";
import { FormControlHelpers } from "./helpers/FormControlHelpers";
import { FormLabelHelpers } from "./helpers/FormLabelHelpers";
import { InputState } from "./Input";
import { StringHelpers } from "../helpers/StringHelpers";
import { ComponentStoreHelpers } from './helpers/ComponentStoreHelpers';
import { AjaxCallHelpers } from './helpers/AjaxCallHelpers';
import { LabelAndPopover } from './components/label-and-popover';
import { PropertyHelpers } from '../helpers/PropertyHelpers';
import { DatasourceLoaderStateService } from '../services/DatasourceLoaderStateService';

registerLocale("nl", nl);
let culture : string = (window as any).currentCultureCode || "";
let importCulture = false;
if (culture.indexOf("en") == 0) {
  culture = "en-US";
  importCulture = true;

} else if (culture.indexOf("-") == 2) {
  culture = culture.substr(0, 2);
  importCulture = true;
}

if (importCulture) {
  import("date-fns/esm/locale").then(module => {
    if (module[culture]) {    
      registerLocale(culture, module[culture]);
    }
  });
}

export interface MaskedInputState {
    dataSourceCounter: number;
    timer: any;
    includeDates: any[];
    showMessageIfNoOptions : string;
    isLoading: boolean;
    hasFocus: boolean;
}

export interface DateTimePickerInputProps {
  name?: string;
  value?: string;
  onChange?: any;
  onBlur? : any;
  onFocus? : any;
  className? : string;

  datetimepicker : any;
  field : any;
  changeHandlerMode: string;
}

class DateTimePickerInput extends React.Component<
  DateTimePickerInputProps
> {
  constructor(props) {
    super(props);
    this.input = React.createRef();    
  }

  input: any;

  closedDatePickerWhenOnIos = false;

  componentDidMount() {
    
    // only on android scroll input to the top to prevent datepicker appearing below
    // the keyboard (on iOS the native datepicker is used, so this isn't needed)
    if (isAndroid()) {
      if (this.input && this.input.current) {
        this.input.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "start"
        });
      }
    }
  }
  
  handleKeyDown = (event) => {
    
    if (event.key === 'Enter') {
        event.preventDefault();
        
        const proxyEvent = {...event, target: {
                ...event.target, 
                id: this.props.field.name, 
                name: this.props.field.name,
                value: this.props.value
            }
        };

        if (this.props.changeHandlerMode == "input") {
            this.props.onChange(this.props.value, proxyEvent);
        } else {
            this.props.onChange(proxyEvent);
        }

        this.props.onBlur(event);

        if (this.props.datetimepicker && this.props.datetimepicker.current) {
            this.props.datetimepicker.current.setOpen(false);
        }
        
        this.props.field.onBlur(proxyEvent);
        this.input.current.blur();
        // fix to fool edge
        // todo : rewrite to vanilla JS
        $("[name=_" + this.props.name + "]").focus();
        $("[name=_" + this.props.name + "]").blur();
        return false;
    }
  }  

  render() {
    const iOS = isIOS();
    let props = {...this.props};
    delete props.changeHandlerMode;
    return (
      <>
        <div className="react-datepicker__input-container">
            <input
            {...props} 
            type={iOS ? "date" : "text"}
            ref={this.input}
            name={this.props.name}
            id={this.props.name}
            className={this.props.className + " form-control react-datepicker__input"}
            value={this.props.value}
            autoComplete="off" 
            maxLength={10}
            onChange={this.props.onChange}
            onBlur={this.props.onBlur}
            onFocus={this.props.onFocus}
            onKeyDown={this.handleKeyDown}
            />
        </div>
        <input className="sr-only dummy-input-to-fool-edge" 
            aria-hidden={true} 
            name={"_" + this.props.name} />
      </>
    );
  }
}

class DatePickerControl extends React.Component<
  FormControlProps,
  MaskedInputState
> {
  constructor(props: FormControlProps) {
    super(props);
    this.container = React.createRef();
    this.datetimepicker = React.createRef();
    this.state = {
      dataSourceCounter: 0,
      includeDates: undefined,
      timer: undefined,
      showMessageIfNoOptions: "",
      isLoading: false,
      hasFocus: false
    };
  }

  container: any;
  datetimepicker: any;

  initDatepicker = () => {
    if (ComponentStoreHelpers.hasDatasource(this)) {            
      ComponentStoreHelpers.getData(this).then((returnData) => {
          if (this.unmounted) {
              return;
          }
          if (returnData) {
            let includeDates = [];
            (returnData as []).map((dateItem : any, index) => {
              includeDates.push(new Date(dateItem.Date));
            });

            let showMessageIfNoOptions = "";
            if (includeDates.length == 0) {
              if (this.props.formFieldDefinition.showMessageIfNoOptions && 
                this.props.formFieldDefinition.showMessageIfNoOptions !== "") {
                DatasourceLoaderStateService.resetLoadingCounter();
                showMessageIfNoOptions = this.props.formFieldDefinition.showMessageIfNoOptions;
              }
            }

            this.setState({includeDates : includeDates, showMessageIfNoOptions : showMessageIfNoOptions}, () => {
              if (!!this.props.formFieldDefinition.autoSetUsingDatasource && 
                  !this.props.field.value) {
                if (includeDates.length > 0) {                  
                  const nlDate = this.getDateAsNlDate(includeDates[0]);

                  FormFieldStateHelpers.updateFormikField((this as unknown as React.Component<FormControlProps , InputState>) , nlDate);
                  if (this.props.formFieldDefinition.isDependentField) {                     
                    //this.props.formikBag.setFieldValue(this.props.formFieldDefinition.name, nlDate);
                    setTimeout(() => {
                      this.props.formController.setState({[this.props.formFieldDefinition.name]: nlDate});
                    }, 10);
                  }    
          
                }
              }
            });
          } else {
            if (this.props.formFieldDefinition.showMessageIfNoOptions && 
              this.props.formFieldDefinition.showMessageIfNoOptions !== "") {
              this.setState({showMessageIfNoOptions : this.props.formFieldDefinition.showMessageIfNoOptions});
            }
          }
      });
    }
  }

  componentDidMount() {
    this.initDatepicker();
  }

  unmounted : boolean = false;

  componentWillUnmount() {
    this.unmounted = true;
  }

  componentDidUpdate(prevProps: any) {
    let triggerGetDatasource =  (this.props.form && JSON.stringify(this.props.form.values) != JSON.stringify(prevProps.form.values));

    if (Array.isArray(this.props.formFieldDefinition.dependentOnField)) {
        this.props.formFieldDefinition.dependentOnField.map(dependentField => {
            let dependentValue = this.props.formikBag.values[dependentField];
            if (dependentValue && (dependentValue as any).value) {
                dependentValue = (dependentValue as any).value;
            }

            if (prevProps && prevProps.formikBag && prevProps.formikBag.values) {
                let prevDependentValue = prevProps.formikBag.values[dependentField];
                if (prevDependentValue && (prevDependentValue as any).value) {
                    prevDependentValue = (prevDependentValue as any).value;
                }

                if (dependentValue != prevDependentValue) {
                    triggerGetDatasource = true;
                }
            } else {
                triggerGetDatasource = true;
            }
        });
    }

    if (triggerGetDatasource) {
      this.initDatepicker();
    }
  }

  shouldComponentUpdate(nextProps: FormControlProps, nextState: MaskedInputState) : boolean {
    /*
        check here component needs to update component
        depending if form values changed that are listed in dependentOnField array/string
        (check nextProps.formikBag.values vs props.formikBag.values)
    */
    let triggerGetDatasource = false;
    if (Array.isArray(this.props.formFieldDefinition.dependentOnField)) {
        this.props.formFieldDefinition.dependentOnField.map(dependentField => {

            if (nextProps && nextProps.formikBag && nextProps.formikBag.values) {
                let dependentValue = nextProps.formikBag.values[dependentField];
                if (dependentValue && (dependentValue as any).value) {
                    dependentValue = (dependentValue as any).value;
                }

                if (this.props && this.props.formikBag && this.props.formikBag.values) {
                    let prevDependentValue = this.props.formikBag.values[dependentField];
                    if (prevDependentValue && (prevDependentValue as any).value) {
                        prevDependentValue = (prevDependentValue as any).value;
                    }

                    if (dependentValue != prevDependentValue) {
                        triggerGetDatasource = true;
                    }
                } else {
                    triggerGetDatasource = true;
                }
            } else {
                triggerGetDatasource = true;
            }
            
        });
    }

    const fieldName: string = this.props.formFieldDefinition.name;
    // TODO: can they be different?
    const nextFieldName: string = nextProps.formFieldDefinition.name;

    let datasourceDiffers = false;
    if (this.props.formFieldDefinition.datasource) {
        datasourceDiffers = this.state[this.props.formFieldDefinition.datasource] != nextState[this.props.formFieldDefinition.datasource];
    }

    let currentError = "";
    if (this.props.form && this.props.form.errors &&
      this.props.form.errors[fieldName]) {
        currentError = this.props.form.errors[fieldName];
    }
    let nextError = "";
    if (nextProps.form && nextProps.form.errors && 
        nextProps.form.errors[nextFieldName]) {
      nextError = nextProps.form.errors[nextFieldName];
    }

    let currentTouched = false;
    if (this.props.form && this.props.form.touched &&
      this.props.form.touched[fieldName]) {
      currentTouched = this.props.form.touched[fieldName];
    }
    let nextTouched = false;
    if (nextProps.form && nextProps.form.touched && 
        nextProps.form.touched[nextFieldName]) {
      nextTouched = nextProps.form.touched[nextFieldName];
    }

    let result = datasourceDiffers ||
        (nextState.showMessageIfNoOptions != this.state.showMessageIfNoOptions) ||
        (this.props.form && JSON.stringify(this.props.form.values) != JSON.stringify(nextProps.form.values)) ||
        this.state.includeDates != nextState.includeDates ||
        this.state.hasFocus != nextState.hasFocus ||
        this.state.dataSourceCounter != nextState.dataSourceCounter ||
        triggerGetDatasource || 
        (nextProps.field.value != this.props.field.value) ||
        (currentError != nextError) ||
        (currentTouched != nextTouched) ||
        (nextProps.formikBag.values[fieldName] != this.props.formikBag.values[fieldName]);

    return result;
  }

  getDateAsJSDate = value => {
    if(this.props.formFieldDefinition.dataType === "date"){
      return new DateParser().parse(value);
    }
    return value;
  }

  getDateAsIsoDate = value => {
    if(this.props.formFieldDefinition.dataType === "date"){
      return new DateFormatter().format(value, {format: 'YYYY-MM-DD'});
    }
  }

  getDateAsNlDate = value => {
    if (this.props.formFieldDefinition.dataType === "date") {
      value = StringHelpers.convertDateToNL(value);
    }
    return value;
  }

  handleChange = (date, event) => {

    if (date === null  || date === undefined) {        
        return;
    }

    if (event && event.persist) {
      event.persist();
    }

    if (event === null  || event === undefined) {
        event = {};
    }
    
    const dateToUse = StringHelpers.isString(date) || StringHelpers.isDate(date) ? date : (date.target ? date.target.value : "");
    const eventToUse = StringHelpers.isString(date) || StringHelpers.isDate(date) ? event : date;
    
    if (eventToUse.persist) {
        eventToUse.persist();
    }

    const nlDate = this.getDateAsNlDate(dateToUse);

    FormFieldStateHelpers.updateFormikField(
        (this as unknown) as React.Component<FormControlProps, InputState>,
        nlDate == "Invalid date" ? dateToUse : nlDate
    );

    if (this.props.field.onBlur) {  
        let newEvent : any = {};

        newEvent.target = {};
        newEvent.target.id = this.props.field.name;
        newEvent.target.name = this.props.field.name;

        this.props.field.onBlur(newEvent);
    }

    if (this.props.formFieldDefinition.isDependentField) {                     
      this.props.formController.setState({[this.props.formFieldDefinition.name]:  nlDate == "Invalid date" ? dateToUse : nlDate});
    }


    if (this.props.formFieldDefinition.triggerDatasource) {
      // Known issue: formik setFieldValue is async. By using a time out, we work around this.
      setTimeout(() => {
          if (FormControlHelpers.isValid(this)) {
              // Only get data when input is valid
              AjaxCallHelpers.getRelatedData(this as any, nlDate == "Invalid date" ? dateToUse : nlDate);
          }
      }, 1);
  }
  }
  
  onInputFocus = (event) => {                
    this.setState({hasFocus : true});
  }

  onInputBlur = (event) => {
    if (event.persist) {
        event.persist();
    }
    this.setState({hasFocus : false}, () => {
        this.props.field.onBlur(event);
    });
  }

  render() {    
    if (this.state.showMessageIfNoOptions !== "") {
      return <>
        <label>{this.state.showMessageIfNoOptions}</label>
        {FormControlHelpers.invalidFeedbackComponent(this)}
      </>;
    }

    return (
      <div
        className={
          FormStylingHelpers.getFormGroupStyle(this.props) +
          " form__section-input " +
          Slug.fromString(this.props.formFieldDefinition.name) +
          "-maskedinput-form-group"
        }
      >
        <LabelAndPopover 
            fieldId={PropertyHelpers.getFieldID(this.props)} 
            label={FormLabelHelpers.getFieldLabel(this)}
            explanation={this.props.formFieldDefinition.explanation}
            hasPopover={this.props.formFieldDefinition.hasPopover}                    
        ></LabelAndPopover>
        <div className="input-group">
          <label className="react-datepicker__label">
            {isIOS() ? <DateTimePickerInput
                    datetimepicker={undefined}
                    field={this.props.field}

                    name={this.props.field.name}
                    value={this.getDateAsIsoDate(this.props.field.value)}
                    onChange={this.handleChange}
                    changeHandlerMode="input"
                    onBlur={this.onInputBlur}
                    onFocus={this.onInputFocus}
                    className={FormStylingHelpers.getInputStyle(this)}
                /> :
            <DatePicker
              {...FormControlHelpers.baseProperties(this)}
              className={FormStylingHelpers.getInputStyle(this)}
              value={this.getDateAsJSDate(this.props.field.value)}
              autoComplete="off"
              locale={culture || "nl"}
              disabled={this.props.formFieldDefinition.datasource && 
                (!this.state.includeDates ||
                  (this.state.includeDates && this.state.includeDates.length == 0))}
              includeDates={this.state.includeDates}
              dateFormat="dd-MM-yyyy"
              dateFormatCalendar={this.props.formFieldDefinition.isBirthDate || this.props.formFieldDefinition.hasYearMonthControls ? " " : "MMMM yyyy"}
              selected={this.getDateAsJSDate(this.props.field.value)}
              onChange={this.handleChange}
              onFocus={this.onInputFocus}
              onBlur={this.onInputBlur}
              ref={this.datetimepicker}
              showMonthDropdown={this.props.formFieldDefinition.isBirthDate || this.props.formFieldDefinition.hasYearMonthControls}
              showYearDropdown={this.props.formFieldDefinition.isBirthDate || this.props.formFieldDefinition.hasYearMonthControls}
              yearDropdownItemNumber={this.props.formFieldDefinition.isBirthDate ? 100 : 15}
              minDate={!!this.props.formFieldDefinition.fromToday ? Moment().toDate() : undefined}
              maxDate={this.props.formFieldDefinition.isBirthDate ? Moment().toDate() : undefined}
              maxMonth={12}
              scrollableYearDropdown={this.props.formFieldDefinition.isBirthDate || this.props.formFieldDefinition.hasYearMonthControls}
              dropdownMode={isIE11() ? "select" : "scroll"}
              customInput={
                <DateTimePickerInput
                    datetimepicker={this.datetimepicker}
                    field={this.props.field}
                    changeHandlerMode="datetimepicker"
                />
              }

              isClearable={false}
              adjustDateOnChange={true}
              onClickOutside={event => {                
                
                if (event.persist) {
                  event.persist();
                }

                this.setState({hasFocus : false}, () => {
                  
                  if (this.props.field.onBlur) {
                    this.props.field.onBlur({...event, target: {
                          ...event.target, 
                          id: this.props.field.name, 
                          name: this.props.field.id
                      }
                    });
                  }
                });
              }}
            />
            }
          </label>
          <div className="input-group-append form-control--is-valid-indicator">
            <div className="input-group-text">
              <span className="icon icon-checkmark" />
            </div>
          </div>
          {!this.state.hasFocus && FormControlHelpers.invalidFeedbackComponent(this)}
        </div>
      </div>
    );
  }
}

export { DatePickerControl };
